import axios from 'axios';
import { FilterConfig, appendFiltersToUrl } from '../utils/apiFilters';
// import { supabase } from '../lib/supabase';

// Always use relative URLs to go through the Vite proxy
const API_BASE_URL = '';  // Empty base URL means relative to current origin

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for session authentication
});

// Commenting out auth interceptor until Supabase is properly configured
// // Add request interceptor to include auth token
// api.interceptors.request.use(
//   async (config) => {
//     const { data: { session } } = await supabase.auth.getSession();
//     if (session?.access_token) {
//       config.headers.Authorization = `Bearer ${session.access_token}`;
//     }
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

// API Response types
export interface Sport {
  id: string;
  name: string;
  slug: string;
  abbreviation: string;
  description: string;
}

export interface Level {
  id: string;
  name: string;
  slug: string;
  abbreviation: string;
  hierarchy: number;
  description: string;
}

export interface Conference {
  id: string;
  name: string;
  slug: string;
  abbreviation: string;
  sport: Sport;
  level: Level;
  region: string;
  founded_year: number;
}

export interface Championship {
  id: string;
  name: string;
  slug: string;
  official_name: string;
  sport: Sport;
  level: Level;
  conference?: Conference;
  first_year: number;
  last_year?: number;
  trophy_name: string;
  description: string;
  meta_title?: string;
  meta_description?: string;
}

export interface Team {
  id: string;
  name: string;
  slug: string;
  nickname?: string;
  abbreviation: string;
  city: string;
  state?: string;
  country?: string;
  sport: Sport;
  level: Level;
  conference: Conference;
  division?: {
    id: string;
    name: string;
    slug: string;
    abbreviation: string;
    conference: Conference;
  };
  home_venue?: {
    id: string;
    name: string;
    slug: string;
    nickname: string;
    city: string;
    state: string;
    country: string;
    capacity: number;
    surface: string;
    opened_year: number;
    latitude: number | null;
    longitude: number | null;
  };
  founded_year: number;
  colors_primary?: string;
  colors_secondary?: string;
  logo_url?: string;
  // Legacy fields for backward compatibility
  sport_name?: string;
  level_name?: string;
  conference_name?: string;
  division_name?: string;
  stadium?: string;
  championships_won?: number;
  last_championship_year?: number;
  short_name?: string;
}

export interface Champion {
  id: string;
  team_name: string;
  team_city: string;
  team_abbreviation: string;
  championship_name: string;
  season_name: string;
  year: number;
  record: string;
  coach: string;
}

// Paginated response interface
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// API Functions with proper pagination handling and site filtering
export const championshipsApi = {
  getAll: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/championships/championships/', filters) : '/api/championships/championships/';
    const response = await api.get<PaginatedResponse<Championship>>(url);
    return { ...response, data: response.data.results }; // Extract results array
  },
  getBySlug: (slug: string) => api.get<Championship>(`/api/championships/championships/${slug}/`),
  getChampions: async (slug: string, filters?: FilterConfig) => {
    const baseUrl = `/api/championships/championships/${slug}/champions/`;
    const url = filters ? appendFiltersToUrl(baseUrl, filters) : baseUrl;
    const response = await api.get<PaginatedResponse<Champion>>(url);
    return { ...response, data: response.data.results };
  },
  getStats: (slug: string) => api.get<any>(`/api/championships/championships/${slug}/stats/`),
  getGames: async (slug: string, filters?: FilterConfig) => {
    const baseUrl = `/api/championships/championships/${slug}/games/`;
    const url = filters ? appendFiltersToUrl(baseUrl, filters) : baseUrl;
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data };
  },
};

export const teamsApi = {
  getAll: async (filters?: FilterConfig) => {
    // Fetch all teams by fetching all pages
    let allTeams: Team[] = [];
    let url = filters ? appendFiltersToUrl('/api/teams/teams/', filters) : '/api/teams/teams/';
    let nextUrl: string | null = url;

    console.log('Starting to fetch teams from:', url);

    // Fetch all pages
    while (nextUrl) {
      const response: Awaited<ReturnType<typeof api.get<PaginatedResponse<Team>>>> = await api.get<PaginatedResponse<Team>>(nextUrl);
      console.log('Fetched page, response.data:', response.data);
      allTeams = [...allTeams, ...response.data.results];
      // Get the next page URL
      nextUrl = response.data.next;
      // If next URL is absolute, make it relative
      if (nextUrl && nextUrl.startsWith('http')) {
        const parsedUrl = new URL(nextUrl);
        nextUrl = parsedUrl.pathname + parsedUrl.search;
      }
    }

    console.log('All teams fetched:', allTeams.length, 'teams');

    // Sort teams alphabetically by name
    allTeams.sort((a, b) => a.name.localeCompare(b.name));

    // Return just the array, not wrapped in an object
    return allTeams;
  },
  getBySlug: async (slug: string) => {
    const response = await api.get<Team>(`/api/teams/teams/${slug}/`);
    return response.data;
  },
  getChampionships: async (slug: string) => {
    const response = await api.get<Champion[]>(`/api/teams/teams/${slug}/championships/`);
    return { data: response.data };
  },
  getRoster: async (slug: string, year?: number) => {
    const url = year 
      ? `/api/teams/teams/${slug}/roster/?year=${year}`
      : `/api/teams/teams/${slug}/roster/`;
    const response = await api.get<any[]>(url);
    return { data: response.data };
  },
  getSchedule: async (slug: string, year?: number) => {
    const url = year
      ? `/api/teams/teams/${slug}/schedule/?year=${year}`
      : `/api/teams/teams/${slug}/schedule/`;
    const response = await api.get<any[]>(url);
    return { data: response.data };
  },
  getStats: async (slug: string, year?: number) => {
    const url = year
      ? `/api/teams/teams/${slug}/stats/?year=${year}`
      : `/api/teams/teams/${slug}/stats/`;
    const response = await api.get<any>(url);
    return { data: response.data };
  },
  getSeasons: async (slug: string) => {
    const response = await api.get<any[]>(`/api/teams/teams/${slug}/seasons/`);
    return { data: response.data };
  },
};

export const championsApi = {
  getAll: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/championships/champions/', filters) : '/api/championships/champions/';
    const response = await api.get<PaginatedResponse<Champion>>(url);
    return { ...response, data: response.data.results };
  },
  getByYear: async (year: number, filters?: FilterConfig) => {
    let url = `/api/championships/champions/?season__start_year=${year}`;
    if (filters) {
      const filterUrl = appendFiltersToUrl('/api/championships/champions/', filters);
      url = `${filterUrl}${filterUrl.includes('?') ? '&' : '?'}season__start_year=${year}`;
    }
    const response = await api.get<PaginatedResponse<Champion>>(url);
    return { ...response, data: response.data.results };
  },
};

export const sportsApi = {
  getAll: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/core/sports/', filters) : '/api/core/sports/';
    const response = await api.get<PaginatedResponse<Sport>>(url);
    return { ...response, data: response.data.results };
  },
};

export const seasonsApi = {
  getAll: async (filters?: FilterConfig) => {
    // Fetch all seasons by fetching all pages
    let allSeasons: any[] = [];
    let url = filters ? appendFiltersToUrl('/api/championships/seasons/', filters) : '/api/championships/seasons/';
    let nextUrl: string | null = url;
    
    // Fetch all pages
    while (nextUrl) {
      const response: Awaited<ReturnType<typeof api.get<PaginatedResponse<any>>>> = await api.get<PaginatedResponse<any>>(nextUrl);
      allSeasons = [...allSeasons, ...response.data.results];
      // Get the next page URL
      nextUrl = response.data.next;
      // If next URL is absolute, make it relative
      if (nextUrl && nextUrl.startsWith('http')) {
        const urlObj: URL = new URL(nextUrl);
        nextUrl = urlObj.pathname + urlObj.search;
      }
    }
    
    // Sort seasons by start year descending (most recent first)
    allSeasons.sort((a, b) => {
      // First sort by start_year descending
      if (b.start_year !== a.start_year) {
        return b.start_year - a.start_year;
      }
      // Then by sport name for same year
      return (a.sport?.name || '').localeCompare(b.sport?.name || '');
    });
    
    return { data: allSeasons };
  },
};

export const gamesApi = {
  getAll: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/games/games/', filters) : '/api/games/games/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results };
  },
  getById: async (id: string) => {
    const response = await api.get<any>(`/api/games/games/${id}/`);
    return { ...response, data: response.data };
  },
  getByDateRange: async (startDate: string, endDate: string, filters?: FilterConfig) => {
    let url = `/api/games/games/?game_date__gte=${startDate}&game_date__lte=${endDate}`;
    if (filters) {
      url = appendFiltersToUrl(url, filters);
    }
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results };
  },
  getToday: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/games/games/today/', filters) : '/api/games/games/today/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getLive: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/games/games/live/', filters) : '/api/games/games/live/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getRecent: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/games/games/recent/', filters) : '/api/games/games/recent/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getUpcoming: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/games/games/upcoming/', filters) : '/api/games/games/upcoming/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getChampionshipGames: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/championships/championship-games/', filters) : '/api/championships/championship-games/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results };
  },
};

export const standingsApi = {
  getAll: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/games/standings/', filters) : '/api/games/standings/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results };
  },
  getCurrent: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/games/standings/current/', filters) : '/api/games/standings/current/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getBySeason: async (year: number, filters?: FilterConfig) => {
    let url = `/api/games/standings/?season=${year}`;
    if (filters) {
      url = appendFiltersToUrl(url, filters);
    }
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getByDivision: async (divisionId?: string, year?: number, filters?: FilterConfig) => {
    let url = `/api/games/standings/by_division/`;
    const params = [];
    if (divisionId) {
      params.push(`division=${divisionId}`);
    }
    if (year) {
      params.push(`season=${year}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    if (filters) {
      url = appendFiltersToUrl(url, filters);
    }
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
};

export const playersApi = {
  getAll: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/people/players/', filters) : '/api/people/players/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results };
  },
  getBySlug: async (slug: string) => {
    const response = await api.get<any>(`/api/people/players/${slug}/`);
    return response.data;
  },
  // Deprecated - use getBySlug instead
  getById: async (slug: string) => {
    return playersApi.getBySlug(slug);
  },
  getActive: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/people/players/active/', filters) : '/api/people/players/active/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getByPosition: async (positionSlug?: string, filters?: FilterConfig) => {
    let url = `/api/people/players/by_position/`;
    if (positionSlug) {
      url += `?position=${positionSlug}`;
    }
    if (filters) {
      url = appendFiltersToUrl(url, filters);
    }
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getRookies: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/people/players/rookies/', filters) : '/api/people/players/rookies/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
};

export const coachesApi = {
  getAll: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/people/coaches/', filters) : '/api/people/coaches/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results };
  },
  getBySlug: async (slug: string) => {
    const response = await api.get<any>(`/api/people/coaches/${slug}/`);
    return response.data;
  },
  // Deprecated - use getBySlug instead
  getById: async (slug: string) => {
    return coachesApi.getBySlug(slug);
  },
  getActive: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/people/coaches/active/', filters) : '/api/people/coaches/active/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
  getHeadCoaches: async (filters?: FilterConfig) => {
    const url = filters ? appendFiltersToUrl('/api/people/coaches/head_coaches/', filters) : '/api/people/coaches/head_coaches/';
    const response = await api.get<PaginatedResponse<any>>(url);
    return { ...response, data: response.data.results || response.data };
  },
};
